/**
 * A fetch wrapper that remembers the ETag and body of every JSON GET per URL, sends
 * `If-None-Match` on the next request for that URL, and turns the server's 304 back into the
 * cached 200 so callers never see a conditional response. The v1 API answers 304 for the
 * library, run summary and run details (`Cache-Control: private, no-cache`).
 */

interface CachedResponse {
  etag: string;
  body: string;
  status: number;
  headers: Array<[string, string]>;
}

export type FetchLike = (input: Request) => Promise<Response>;

export function createEtagFetch(baseFetch?: FetchLike): FetchLike & { clear: () => void } {
  const cache = new Map<string, CachedResponse>();
  const run: FetchLike = async (request) => {
    const fetchImpl = baseFetch ?? ((req: Request) => globalThis.fetch(req));
    if (request.method !== "GET" && request.method !== "HEAD") return fetchImpl(request);
    const key = request.url;
    const cached = cache.get(key);
    let outgoing = request;
    if (cached && !request.headers.has("If-None-Match")) {
      outgoing = new Request(request, { headers: new Headers(request.headers) });
      outgoing.headers.set("If-None-Match", cached.etag);
    }
    const response = await fetchImpl(outgoing);
    if (response.status === 304) {
      if (!cached) return response;
      const headers = new Headers(cached.headers);
      // Carry over per-response headers the server sends with a 304 (a refreshed Cache-Control).
      response.headers.forEach((value, name) => headers.set(name, value));
      headers.set("x-instrukt-cache", "etag");
      return new Response(cached.body, { status: cached.status, headers });
    }
    const etag = response.headers.get("ETag");
    const type = response.headers.get("Content-Type") ?? "";
    if (response.ok && etag && type.includes("json")) {
      const body = await response.clone().text();
      const headers: Array<[string, string]> = [];
      response.headers.forEach((value, name) => headers.push([name, value]));
      cache.set(key, { etag, body, status: response.status, headers });
    } else if (!response.ok) {
      cache.delete(key);
    }
    return response;
  };
  return Object.assign(run, {
    clear: () => {
      cache.clear();
    },
  });
}

/** The app-wide instance used by the API client. */
export const etagFetch = createEtagFetch();
